import axios from 'axios';
import {
    AuthResponse,
    User,
    IGCFile,
    FlightData,
    FlightStatistics,
    FlightTrackPoint,
    FilterOptions,
    Glider,
    Pagination,
    FlightsListApiResponse,
    FlightDetailApiResponse,
    CreateFlightApiResponse,
    FlightApiResponse,
    FavoriteBalise,
    FavoriteBaliseCreateRequest,
    FavoriteBaliseReorderRequest,
    FavoriteMeteoSpot,
    FavoriteMeteoSpotCreateRequest,
    FavoriteMeteoSpotUpdateRequest,
    FavoriteMeteoSpotReorderRequest
} from '../types';
import { config } from '../config';
import { parseJwtPayload } from '../utils/jwt';
import { createLogger } from './logger';

// Use the API base URL from the configuration
const API_URL = config.apiBaseUrl;
const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const AUTH_STORAGE_MODE_KEY = 'auth_storage_mode';
const OAUTH_PENDING_REMEMBER_ME_KEY = 'oauth_pending_remember_me';

type AuthStorageMode = 'local' | 'session';

const logger = createLogger('api');

export const AUTH_LOGIN_REDIRECT_EVENT = 'auth:login-required';

const getStorageByMode = (mode: AuthStorageMode): Storage => (mode === 'local' ? localStorage : sessionStorage);

const getStoredMode = (): AuthStorageMode | null => {
    const mode = localStorage.getItem(AUTH_STORAGE_MODE_KEY) || sessionStorage.getItem(AUTH_STORAGE_MODE_KEY);
    return mode === 'local' || mode === 'session' ? mode : null;
};

const getActiveAuthStorage = (): Storage => {
    const storedMode = getStoredMode();
    if (storedMode) {
        return getStorageByMode(storedMode);
    }

    if (localStorage.getItem(ACCESS_TOKEN_KEY) || localStorage.getItem(REFRESH_TOKEN_KEY)) {
        return localStorage;
    }

    if (sessionStorage.getItem(ACCESS_TOKEN_KEY) || sessionStorage.getItem(REFRESH_TOKEN_KEY)) {
        return sessionStorage;
    }

    return localStorage;
};

export const getStoredAccessToken = (): string | null => {
    return localStorage.getItem(ACCESS_TOKEN_KEY) ?? sessionStorage.getItem(ACCESS_TOKEN_KEY);
};

export const getStoredRefreshToken = (): string | null => {
    return localStorage.getItem(REFRESH_TOKEN_KEY) ?? sessionStorage.getItem(REFRESH_TOKEN_KEY);
};

export const clearStoredAuthTokens = (): void => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(AUTH_STORAGE_MODE_KEY);
    sessionStorage.removeItem(ACCESS_TOKEN_KEY);
    sessionStorage.removeItem(REFRESH_TOKEN_KEY);
    sessionStorage.removeItem(AUTH_STORAGE_MODE_KEY);
};

export const storeAuthTokens = (token: string, refreshToken?: string, rememberMe: boolean = true): void => {
    clearStoredAuthTokens();

    const mode: AuthStorageMode = rememberMe ? 'local' : 'session';
    const storage = getStorageByMode(mode);

    storage.setItem(ACCESS_TOKEN_KEY, token);
    if (refreshToken) {
        storage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }
    storage.setItem(AUTH_STORAGE_MODE_KEY, mode);
};

export const setPendingOAuthRememberMe = (rememberMe: boolean): void => {
    sessionStorage.setItem(OAUTH_PENDING_REMEMBER_ME_KEY, rememberMe ? '1' : '0');
};

export const consumePendingOAuthRememberMe = (): boolean | null => {
    const value = sessionStorage.getItem(OAUTH_PENDING_REMEMBER_ME_KEY);
    if (value === null) {
        return null;
    }

    sessionStorage.removeItem(OAUTH_PENDING_REMEMBER_ME_KEY);
    return value === '1';
};

export const updateStoredAuthTokens = (token: string, refreshToken?: string): void => {
    const storage = getActiveAuthStorage();
    const mode: AuthStorageMode = storage === sessionStorage ? 'session' : 'local';

    clearStoredAuthTokens();

    storage.setItem(ACCESS_TOKEN_KEY, token);
    if (refreshToken) {
        storage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }
    storage.setItem(AUTH_STORAGE_MODE_KEY, mode);
};

export const dispatchAuthLoginRedirect = (reason: string): void => {
    window.dispatchEvent(
        new CustomEvent(AUTH_LOGIN_REDIRECT_EVENT, {
            detail: { reason }
        })
    );
};

// Create axios instance with base URL and CORS settings that match the backend configuration
export const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    // Set withCredentials to false to match the backend config's allow_credentials: false
    withCredentials: false
});

const refreshApi = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    withCredentials: false
});

// Export api as apiClient for compatibility with existing imports
export const apiClient = api;

// Add request interceptor to add auth token to requests
api.interceptors.request.use(
    (config) => {
        // Ne pas ajouter de token pour les endpoints d'authentification
        const isAuthEndpoint = config.url?.includes('/api/auth/token/') || 
                              config.url?.includes('/api/auth/register') || 
                              config.url?.includes('/api/auth/login');
        
        if (!isAuthEndpoint) {
            const token = getStoredAccessToken();
            if (token) {
                config.headers['Authorization'] = `Bearer ${token}`;
                logger.debug('Adding token to request', { url: config.url });
            } else {
                logger.debug('No token available for request', { url: config.url });
            }
        } else {
            logger.debug('Skipping token for auth endpoint', { url: config.url });
        }
        return config;
    },
    (error) => {
        logger.error('Request interceptor error', error);
        return Promise.reject(error);
    }
);

// Utility function to check if token needs refresh (expires in less than 5 minutes)
export const shouldRefreshToken = (): boolean => {
    const token = getStoredAccessToken();
    if (!token) return false;

    try {
        const payload = parseJwtPayload<{ exp?: number }>(token);
        if (!payload?.exp) {
            return false;
        }

        const currentTime = Date.now() / 1000; // Convert to seconds
        const timeUntilExpiry = payload.exp - currentTime;

        // Return true if token expires in less than 5 minutes (300 seconds) but is still valid
        return timeUntilExpiry < 300 && timeUntilExpiry > 0;
    } catch (error) {
        logger.warn('Error checking token expiration', error);
    }

    return false;
};

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let failedQueue: Array<{ resolve: (value?: unknown) => void; reject: (reason?: unknown) => void }> = [];

// Function to process queue on successful refresh
const processQueue = (error: Error | null, token: string | null = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    
    failedQueue = [];
};

// Add response interceptor to handle token expiration (401 errors)
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // Vérifier si l'erreur est une erreur 401 (non autorisé)
        if (error.response && error.response.status === 401) {
            logger.debug('Received 401 error', { url: originalRequest.url });

            // Check if the request was to refresh endpoint itself
            if (originalRequest.url?.includes('/api/auth/refresh')) {
                logger.debug('Refresh endpoint itself returned 401, refresh token is invalid');
                isRefreshing = false;
                processQueue(new Error('Refresh token invalid'), null);
                clearStoredAuthTokens();
                dispatchAuthLoginRedirect('refresh-endpoint-401');
                return Promise.reject(error);
            }

            // Check if this request has already been retried
            if (originalRequest._isRetry) {
                logger.debug('Request already retried, rejecting');
                return Promise.reject(error);
            }

            const refreshToken = getStoredRefreshToken();
            
            if (!refreshToken) {
                logger.debug('No refresh token available, redirecting to login');
                clearStoredAuthTokens();
                dispatchAuthLoginRedirect('missing-refresh-token');
                return Promise.reject(error);
            }

            // If already refreshing, queue this request
            if (isRefreshing) {
                logger.debug('Token refresh already in progress, queuing request');
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                })
                    .then((token) => {
                        originalRequest.headers['Authorization'] = `Bearer ${token}`;
                        originalRequest._isRetry = true;
                        return api(originalRequest);
                    })
                    .catch((err) => {
                        return Promise.reject(err);
                    });
            }

            logger.debug('Starting token refresh process');
            originalRequest._isRetry = true;
            isRefreshing = true;

            try {
                logger.debug('Calling refresh endpoint with refresh token');
                const data = await authService.refreshToken();

                if (data?.token) {
                    logger.debug('Token refreshed successfully');

                    // Update the original request with new token
                    originalRequest.headers['Authorization'] = `Bearer ${data.token}`;

                    // Process the queue with the new token
                    processQueue(null, data.token);

                    isRefreshing = false;

                    // Retry the original request
                    return api(originalRequest);
                }

                throw new Error('Refresh endpoint returned no token');
            } catch (refreshError) {
                logger.error('Token refresh failed in interceptor', refreshError);
                isRefreshing = false;
                processQueue(refreshError as Error, null);
                clearStoredAuthTokens();
                dispatchAuthLoginRedirect('refresh-failed');
                return Promise.reject(refreshError);
            }
        }
        
        return Promise.reject(error);
    }
);

// Auth services
export const authService = {
    login: async (email: string, password: string, rememberMe: boolean = false): Promise<AuthResponse> => {
        const response = await api.post<AuthResponse>('/api/auth/login', { 
            email, 
            password, 
            rememberMe 
        });
        
        storeAuthTokens(response.data.token, response.data.refresh_token, rememberMe);
        
        return response.data;
    },

    // Google authentication with remember me support
    loginWithGoogle: async (rememberMe: boolean = false): Promise<string> => {
        setPendingOAuthRememberMe(rememberMe);

        // Get the auth URL but specify our frontend callback URL
        const frontendRedirectUri = window.location.origin + '/auth/google/callback';

        // Call the backend to get the Google auth URL
        const response = await api.get<{ auth_url: string }>(
            '/api/auth/token/google_main',
            { params: { redirect_uri: frontendRedirectUri } }
        );

        return response.data.auth_url;
    },

    // Nouvelle méthode pour l'authentification directe avec un token Google
    loginWithGoogleToken: async (idToken: string, rememberMe: boolean = false): Promise<AuthResponse> => {
        const response = await api.post<AuthResponse>(
            `/api/auth/token/google_main/${rememberMe}`,
            { 
                idToken,
                rememberMe 
            }
        );

        if (response.data && response.data.token) {
            storeAuthTokens(response.data.token, response.data.refresh_token, rememberMe);
        }

        return response.data;
    },

    // OAuth callback: exchange authorization code for application tokens
    loginWithGoogleCode: async (code: string, state?: string, rememberMe?: boolean): Promise<AuthResponse> => {
        const frontendRedirectUri = window.location.origin + '/auth/google/callback';
        const response = await api.get<AuthResponse>('/api/auth/token/google_main', {
            params: {
                code,
                state,
                redirect_uri: frontendRedirectUri
            }
        });

        if (response.data?.token) {
            if (typeof rememberMe === 'boolean') {
                storeAuthTokens(response.data.token, response.data.refresh_token, rememberMe);
            } else {
                updateStoredAuthTokens(response.data.token, response.data.refresh_token);
            }
        }

        return response.data;
    },

    // Register a new user with email and password
    register: async (email: string, password: string, givenName: string, familyName: string): Promise<AuthResponse> => {
        const response = await api.post<AuthResponse>('/api/auth/register', {
            email,
            password,
            givenName,
            familyName
        });

        // Store token if registration automatically logs in the user
        if (response.data && response.data.token) {
            updateStoredAuthTokens(response.data.token, response.data.refresh_token);
        }

        return response.data;
    },

    // Request password reset - sends reset email
    requestPasswordReset: async (email: string): Promise<{ message: string }> => {
        const response = await api.post('/api/auth/request-password-reset', { email });
        return response.data;
    },

    // Reset password using token received by email
    resetPassword: async (token: string, newPassword: string): Promise<{ success: boolean, message: string }> => {
        const response = await api.post('/api/auth/reset-password', {
            token,
            newPassword
        });
        return response.data;
    },

    // Handle JWT token from Google auth callback
    handleAuthCallback: (token: string, refreshToken?: string): void => {
        updateStoredAuthTokens(token, refreshToken);
    },

    // Refresh the JWT token using refresh token
    refreshToken: async (): Promise<AuthResponse> => {
        const refreshToken = getStoredRefreshToken();
        if (!refreshToken) {
            throw new Error('No refresh token available');
        }

        const response = await refreshApi.post<AuthResponse>('/api/auth/refresh', {
            refresh_token: refreshToken
        });
        
        if (response.data && response.data.token) {
            updateStoredAuthTokens(response.data.token, response.data.refresh_token);
        }
        
        return response.data;
    },

    logout: (): void => {
        clearStoredAuthTokens();
    },

    getCurrentUser: async (): Promise<User> => {
        const response = await api.get<User>('/api/users/profile');
        return response.data;
    },



};

// IGC file services
export const igcService = {
    uploadIGCFile: async (file: File, onProgress?: (progress: number) => void): Promise<IGCFile> => {
        const formData = new FormData();
        formData.append('igcFile', file);  // Utiliser le nom de paramètre 'igcFile' attendu par l'API (camelCase)

        const response = await api.post<IGCFile>('/api/flights/upload-igc', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            onUploadProgress: onProgress ? (progressEvent) => {
                const percentCompleted = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 100));
                onProgress(percentCompleted);
            } : undefined,
        });
        return response.data;
    },

    // Nouvelle méthode pour uploader plusieurs fichiers IGC
    uploadMultipleIGCFiles: async (files: File[], onProgress?: (progress: number) => void, gliderId?: string): Promise<IGCFile[]> => {
        const formData = new FormData();

        // Append each file to formData with the name 'igc_files[]'
        files.forEach(file => {
            formData.append('igcFiles[]', file);
        });

        // Append the glider ID if provided
        if (gliderId) {
            formData.append('gliderId', gliderId);
        }

        const response = await api.post<IGCFile[]>('/api/flights/upload-igc', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            onUploadProgress: onProgress ? (progressEvent) => {
                const percentCompleted = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 100));
                onProgress(percentCompleted);
            } : undefined,
        });

        return response.data;
    },

    getIGCFileById: async (id: string): Promise<IGCFile> => {
        const response = await api.get<IGCFile>(`/api/igc/files/${id}`);
        return response.data;
    },

    deleteIGCFile: async (id: string): Promise<void> => {
        await api.delete(`/api/igc/files/${id}`);
    },

    downloadIGCFile: async (id: string): Promise<Blob> => {
        const response = await api.get(`/api/igc/files/${id}/download`, {
            responseType: 'blob',
        });
        return response.data;
    },

    // Parse an uploaded IGC file to extract flight data
    parseIGCFile: async (id: string): Promise<FlightData> => {
        const response = await api.post<FlightData>(`/api/igc/files/${id}/parse`);
        return response.data;
    },
};


/**
 * L'API attend les types de vol dans un seul paramètre `flightType=cross,thermal` ;
 * un tableau passé tel quel à axios produirait `flightType[]=cross&flightType[]=thermal`,
 * que le backend ignore silencieusement (le filtre serait alors sans effet).
 */
const serializeFlightFilters = <T extends FilterOptions>(filters?: T) => {
    if (!filters) return filters;

    const { flightType, ...rest } = filters;

    return flightType && flightType.length > 0
        ? { ...rest, flightType: flightType.join(',') }
        : rest;
};

const populateData = (flightData: FlightApiResponse, id: number): FlightData => {
    return {
        id: String(id),
        // Récupérer les dates du flightData soumis (camelCase)
        date: flightData.startDate ? flightData.startDate.split('T')[0] : '', // Handle ISO date format
        takeoffTime: flightData.startDate || '',
        landingTime: flightData.endDate || '',
        duration: Math.round((flightData.flightDurationSecond || 0) / 60), // Convertir secondes en minutes
        flightDurationSecond: flightData.flightDurationSecond || 0,
        takeoffLocation: flightData.takeoffLocation || '',
        landingLocation: flightData.landingLocation || '',
        maxAltitude: flightData.maxAltitude || 0,
        minAltitude: flightData.minAltitude || 0,
        distance: flightData.totalDistanceM ? Math.round(flightData.totalDistanceM / 100) / 10 : 0, // Convert to km with 1 decimal
        notes: flightData.notes || '',
        // Champs supplémentaires avec valeurs par défaut
        maxClimbRate: flightData.maxClimbRate || 0,
        maxSinkRate: flightData.maxSinkRate || 0,
        maxSpeed: flightData.maxSpeed || 0,
        averageSpeed: flightData.averageSpeed || 0,
        totalDistanceM: flightData.totalDistanceM || 0,
        pointToPointDistanceM: flightData.pointToPointDistanceM || 0,
        freeDistanceM: flightData.freeDistanceM ?? null,
        xcScore: flightData.xcScore ?? null,
        xcSolutionType: flightData.xcSolutionType ?? null,
        xcScoringRule: flightData.xcScoringRule ?? null,
        startAltitude: flightData.startAltitude || 0,
        endAltitude: flightData.endAltitude || 0,
        startLatitude: flightData.startLatitude || '',
        startLongitude: flightData.startLongitude || '',
        endLatitude: flightData.endLatitude || '',
        endLongitude: flightData.endLongitude || '',
        glider: flightData.glider?.name || '',
        gliderId: flightData.glider?.id || 0,
        userId: '', // Non fourni dans cette API
        isParsed: flightData.flightFile?.isParsed || false,
        isScoreParsed: flightData.flightFile?.isScoreParsed || false,
        windDirectionAverage: flightData.windDirectionAverage || 0,
        windSpeedAverage: flightData.windSpeedAverage || 0,
        trackImagePath: flightData.trackImagePath || undefined,
        flightType: flightData.flightType ?? null,
        flightTypeSource: flightData.flightTypeSource ?? null,
        points: [] // Initialiser avec un tableau vide, les points peuvent être ajoutés plus tard
    }
}

// Flight data services
export const flightService = {
    createFlight: async (flightData: Partial<FlightData>): Promise<FlightData> => {
        const response = await api.post<CreateFlightApiResponse>('/api/flights', flightData);

        // Vérifier si la réponse est au format de succès avec flight_id (snake) ou flightId (camel)
        const resp = response.data as CreateFlightApiResponse;
        const returnedId = resp?.flight_id ?? resp?.flightId ?? undefined;
        if (resp && resp.success && returnedId) {
            // Use a safe accessor for optional camelCase fields sent from the frontend
            const fd = flightData as unknown as Record<string, unknown>;
            const getString = (k: string, fallback = ''): string => typeof fd[k] === 'string' ? (fd[k] as string) : fallback;
            const getNumber = (k: string, fallback = 0): number => typeof fd[k] === 'number' ? (fd[k] as number) : fallback;
            const flightDurationSeconds = getNumber('flightDurationSeconds', getNumber('flightDurationSecond', 0));
            const points = Array.isArray(fd['points']) ? (fd['points'] as unknown as FlightTrackPoint[]) : (flightData.points || []);

            return {
                id: String(returnedId),
                date: getString('startDate', flightData.date || ''),
                takeoffTime: getString('startDate', flightData.takeoffTime || ''),
                landingTime: getString('endDate', flightData.landingTime || ''),
                duration: Math.round((flightDurationSeconds || 0) / 60),
                flightDurationSecond: flightDurationSeconds || 0,
                takeoffLocation: getString('takeoffLocation', flightData.takeoffLocation || ''),
                landingLocation: getString('landingLocation', flightData.landingLocation || ''),
                maxAltitude: getNumber('maxAltitude', flightData.maxAltitude || 0),
                minAltitude: getNumber('minAltitude', flightData.minAltitude || 0),
                distance: getNumber('distance', flightData.distance || 0),
                notes: getString('notes', flightData.notes || ''),
                maxClimbRate: getNumber('maxClimbRate', flightData.maxClimbRate || 0),
                maxSinkRate: getNumber('maxSinkRate', flightData.maxSinkRate || 0),
                maxSpeed: getNumber('maxSpeed', flightData.maxSpeed || 0),
                averageSpeed: getNumber('averageSpeed', flightData.averageSpeed || 0),
                totalDistanceM: getNumber('totalDistanceM', flightData.totalDistanceM || 0),
                pointToPointDistanceM: getNumber('pointToPointDistanceM', flightData.pointToPointDistanceM || 0),
                freeDistanceM: flightData.freeDistanceM ?? null,
                xcScore: flightData.xcScore ?? null,
                xcSolutionType: flightData.xcSolutionType ?? null,
                xcScoringRule: flightData.xcScoringRule ?? null,
                startAltitude: getNumber('startAltitude', flightData.startAltitude || 0),
                endAltitude: getNumber('endAltitude', flightData.endAltitude || 0),
                startLatitude: getString('startLatitude', flightData.startLatitude || ''),
                startLongitude: getString('startLongitude', flightData.startLongitude || ''),
                endLatitude: getString('endLatitude', flightData.endLatitude || ''),
                endLongitude: getString('endLongitude', flightData.endLongitude || ''),
                glider: getString('glider', flightData.glider || ''),
                gliderId: getNumber('gliderId', flightData.gliderId || 0),
                userId: getString('userId', flightData.userId || ''),
                isParsed: typeof fd['isParsed'] === 'boolean' ? (fd['isParsed'] as boolean) : (flightData.isParsed || false),
                isScoreParsed: typeof fd['isScoreParsed'] === 'boolean' ? (fd['isScoreParsed'] as boolean) : (flightData.isScoreParsed || false),
                windDirectionAverage: getNumber('windDirectionAverage', flightData.windDirectionAverage || 0),
                windSpeedAverage: getNumber('windSpeedAverage', flightData.windSpeedAverage || 0),
                points: points
            };
        } else if (response.data && response.data.flight) {
            // Format alternatif où l'API renvoie un objet flight complet
            const flight = response.data.flight;
            return populateData(flight, flight.id);
        } else {
            // Si le format de réponse est complètement différent, lancer une erreur
            throw new Error('Unexpected API response format: ' + JSON.stringify(response.data));
        }
    },

    getFlights: async (filters?: FilterOptions & { page?: number; limit?: number; sort?: string; direction?: string }): Promise<{ flights: FlightData[]; pagination: Pagination }> => {
        try {
            const response = await api.get<FlightsListApiResponse>('/api/flights', {
                params: serializeFlightFilters(filters)
            });

            if (response.data && response.data.flights && response.data.pagination) {
                const flights = response.data.flights.map(flight => ({
                    id: String(flight.id),
                    date: flight.startDate ? flight.startDate.split('T')[0] : '', // Handle ISO date format
                    takeoffTime: flight.startDate || '',
                    landingTime: flight.endDate || '',
                    duration: Math.round(flight.flightDurationSecond / 60), // Note: singular 'flightDurationSecond'
                    flightDurationSecond: flight.flightDurationSecond, // Keep original for compatibility
                    takeoffLocation: flight.takeoffLocation || '',
                    landingLocation: flight.landingLocation || '',
                    maxAltitude: flight.maxAltitude || 0,
                    minAltitude: flight.minAltitude || 0,
                    distance: flight.totalDistanceM ? Math.round(flight.totalDistanceM / 100) / 10 : 0, // Convert to km with 1 decimal
                    notes: flight.notes || '',
                    maxClimbRate: flight.maxClimbRate || 0,
                    maxSinkRate: flight.maxSinkRate || 0,
                    maxSpeed: flight.maxSpeed || 0,
                    averageSpeed: flight.averageSpeed || 0,
                    totalDistanceM: flight.totalDistanceM || 0,
                    pointToPointDistanceM: flight.pointToPointDistanceM || 0, // Nouveau champ
                    freeDistanceM: flight.freeDistanceM ?? null,
                    xcScore: flight.xcScore ?? null,
                    xcSolutionType: flight.xcSolutionType ?? null,
                    xcScoringRule: flight.xcScoringRule ?? null,
                    startAltitude: flight.startAltitude || 0,
                    endAltitude: flight.endAltitude || 0,
                    startLatitude: flight.startLatitude || '',
                    startLongitude: flight.startLongitude || '',
                    endLatitude: flight.endLatitude || '',
                    endLongitude: flight.endLongitude || '',
                    glider: flight.glider?.name || '', // Glider is now an object
                    gliderId: flight.glider?.id || 0,
                    userId: '', // Non fourni dans cette API
                    isParsed: flight.flightFile?.isParsed || false,
                    isScoreParsed: flight.flightFile?.isScoreParsed || false,
                    windDirectionAverage: flight.windDirectionAverage || 0, // Nouveau champ
                    windSpeedAverage: flight.windSpeedAverage || 0, // Nouveau champ
                    trackImagePath: flight.trackImagePath || undefined,
                    flightType: flight.flightType ?? null,
                    flightTypeSource: flight.flightTypeSource ?? null,
                    points: []
                }));
                const pagination: Pagination = {
                    totalItems: response.data.pagination.totalItems,
                    totalPages: response.data.pagination.totalPages,
                    currentPage: response.data.pagination.currentPage,
                    itemsPerPage: response.data.pagination.itemsPerPage
                };
                return { flights, pagination };
            } else {
                logger.warn('API response for flights has unexpected format', response.data);
                return { flights: [], pagination: { totalItems: 0, totalPages: 0, currentPage: 1, itemsPerPage: 10 } };
            }
        } catch (error) {
            logger.error('Error fetching flights', error);
            throw error;
        }
    },

    getFlightById: async (id: string): Promise<FlightData> => {
        const response = await api.get<FlightDetailApiResponse>(`/api/flights/${id}`);

        // Vérifier si la réponse contient un objet "flight"
        if (response.data && response.data.flight) {
            const flight = response.data.flight;
            const points = response.data.points || [];

            // Convertir les données du format API vers le format frontend (camelCase)
            return {
                id: String(flight.id),
                date: flight.startDate ? flight.startDate.split('T')[0] : '', // Handle ISO date format
                takeoffTime: flight.startDate || '',
                landingTime: flight.endDate || '',
                duration: Math.round((flight.flightDurationSecond || 0) / 60),
                flightDurationSecond: flight.flightDurationSecond || 0,
                takeoffLocation: flight.takeoffLocation || '',
                landingLocation: flight.landingLocation || '',
                maxAltitude: flight.maxAltitude || 0,
                minAltitude: flight.minAltitude || 0,
                distance: flight.totalDistanceM ? Math.round(flight.totalDistanceM / 100) / 10 : 0, // Convert to km with 1 decimal

                // Champs de distances spécifiques (en mètres)
                
                totalDistanceM: flight.totalDistanceM || 0,
                pointToPointDistanceM: flight.pointToPointDistanceM || 0, // Nouveau champ
                freeDistanceM: flight.freeDistanceM ?? null,
                xcScore: flight.xcScore ?? null,
                xcSolutionType: flight.xcSolutionType ?? null,
                xcScoringRule: flight.xcScoringRule ?? null,

                // Altitudes de décollage et d'atterrissage
                startAltitude: flight.startAltitude || 0,
                endAltitude: flight.endAltitude || 0,

                // Coordonnées GPS
                startLatitude: flight.startLatitude || '',
                startLongitude: flight.startLongitude || '',
                endLatitude: flight.endLatitude || '',
                endLongitude: flight.endLongitude || '',

                // Informations complémentaires
                notes: flight.notes || '',

                // Performances et vitesses
                maxClimbRate: flight.maxClimbRate || 0,
                maxSinkRate: flight.maxSinkRate || 0,
                maxSpeed: flight.maxSpeed || 0,
                averageSpeed: flight.averageSpeed || 0,

                // Informations sur l'équipement et statut
                glider: flight.glider?.name || '',
                gliderId: flight.glider?.id || 0,
                userId: '', // Non fourni dans cette API
                isParsed: flight.flightFile?.isParsed || false,
                isScoreParsed: flight.flightFile?.isScoreParsed || false,
                windDirectionAverage: flight.windDirectionAverage || 0,
                windSpeedAverage: flight.windSpeedAverage || 0,
                trackImagePath: flight.trackImagePath || undefined,
                flightType: flight.flightType ?? null,
                flightTypeSource: flight.flightTypeSource ?? null,
                // GeoJSON de la figure optimisée (triangle, aller-retour...), null tant que non scoré
                geojsonScoreData: flight.geojsonScoreData ?? null,

                points: points || [],
            };
        } else {
            // Si le format de réponse est différent, lancer une erreur
            throw new Error('Unexpected API response format for flight details: ' + JSON.stringify(response.data));
        }
    },

    updateFlight: async (id: string, flightData: Partial<FlightData>): Promise<FlightData> => {
        const response = await api.put<FlightData>(`/api/flights/${id}`, flightData);
        return response.data;
    },

    // Update only the notes field of a flight
    updateFlightNotes: async (id: string, notes: string): Promise<FlightData> => {
        const response = await api.put<FlightData>(`/api/flights/${id}`, { notes });
        return response.data;
    },

    deleteFlight: async (id: string): Promise<void> => {
        await api.delete(`/api/flights/${id}`);
    },

    // Get flight statistics for the user
    getFlightStatistics: async (filters?: FilterOptions): Promise<FlightStatistics> => {
        const response = await api.get<FlightStatistics>('/api/flights/statistics', {
            params: serializeFlightFilters(filters)
        });
        return response.data;
    },

    // Récupération des données du dashboard
    async getDashboardData(): Promise<DashboardData> {
        const response = await apiClient.get<DashboardData>('/api/flights/dashboard');
        return response.data;
    },

    generateShareToken: async (flightId: string, durationHours?: number): Promise<{ shareToken: string }> => {
        const payload = durationHours ? { durationHours: durationHours } : {};
        const response = await api.post<{ share_token: string }>(`/api/flights/${flightId}/share`, payload);
        return { shareToken: response.data.share_token };
    },


    getPublicFlightByToken: async (token: string): Promise<FlightData> => {
        // Create a new axios instance for public requests, without the auth interceptor
        const publicApi = axios.create({
            baseURL: API_URL, // API_URL is defined in this file
            headers: {
                'Content-Type': 'application/json',
            },
            withCredentials: false
        });

        const response = await publicApi.get<FlightDetailApiResponse>(`/api/public/flights/${token}`);
        // Vérifier si la réponse contient un objet "flight"
        if (response.data && response.data.flight) {
            const flight = response.data.flight;
            const points = response.data.points || [];
            // Convertir les données du format API vers le format frontend (camelCase)
            return {
                id: String(flight.id),
                date: flight.startDate ? flight.startDate.split('T')[0] : '', // Handle ISO date format
                takeoffTime: flight.startDate || '',
                landingTime: flight.endDate || '',
                duration: Math.round((flight.flightDurationSecond || 0) / 60),
                flightDurationSecond: flight.flightDurationSecond || 0,
                takeoffLocation: flight.takeoffLocation || '',
                landingLocation: flight.landingLocation || '',
                maxAltitude: flight.maxAltitude || 0,
                minAltitude: flight.minAltitude || 0,
                distance: flight.totalDistanceM ? Math.round(flight.totalDistanceM / 100) / 10 : 0, // Convert to km with 1 decimal

                // Champs de distances spécifiques (en mètres)
                
                totalDistanceM: flight.totalDistanceM || 0,
                pointToPointDistanceM: flight.pointToPointDistanceM || 0, // Nouveau champ
                freeDistanceM: flight.freeDistanceM ?? null,
                xcScore: flight.xcScore ?? null,
                xcSolutionType: flight.xcSolutionType ?? null,
                xcScoringRule: flight.xcScoringRule ?? null,

                // Altitudes de décollage et d'atterrissage
                startAltitude: flight.startAltitude || 0,
                endAltitude: flight.endAltitude || 0,

                // Coordonnées GPS
                startLatitude: flight.startLatitude || '',
                startLongitude: flight.startLongitude || '',
                endLatitude: flight.endLatitude || '',
                endLongitude: flight.endLongitude || '',

                // Informations complémentaires
                notes: flight.notes || '',

                // Performances et vitesses

                maxClimbRate: flight.maxClimbRate || 0,
                maxSinkRate: flight.maxSinkRate || 0,
                maxSpeed: flight.maxSpeed || 0,
                averageSpeed: flight.averageSpeed || 0,

                // Type de vol (lecture seule côté partage public)
                flightType: flight.flightType ?? null,
                flightTypeSource: flight.flightTypeSource ?? null,

                // Informations sur l'équipement et statut
                glider: flight.glider?.name || '',
                gliderId: flight.glider?.id || 0,
                userId: '', // Non fourni dans cette API
                isParsed: flight.flightFile?.isParsed || false,
                windDirectionAverage: flight.windDirectionAverage || 0,
                windSpeedAverage: flight.windSpeedAverage || 0,

                points: points || [],
            };
        } else {
            // Si le format de réponse est différent, lancer une erreur
            throw new Error('Unexpected API response format for flight details: ' + JSON.stringify(response.data));
        }
    },

    // Get flight score data as GeoJSON
    getFlightScoreGeoJSON: async (id: string): Promise<{
        success: boolean;
        scoreData: Record<string, unknown>;
        geojsonFilename: string;
        fileSize: number;
        geojsonData?: Record<string, unknown>
    }> => {
        const response = await api.get<{
            success: boolean;
            scoreData: Record<string, unknown>;
            geojsonFilename: string;
            fileSize: number;
            geojsonData?: Record<string, unknown>;
        }>(`/api/flights/${id}/score/geojson`);
        return response.data;
    },
};

// Utility for handling errors
export const handleApiError = (error: unknown): string => {
    if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response: { data: { message?: string }; status: number } };
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        return axiosError.response.data.message || `Error: ${axiosError.response.status}`;
    } else if (error && typeof error === 'object' && 'request' in error) {
        // The request was made but no response was received
        return 'No response from server. Please check your internet connection.';
    } else {
        // Something happened in setting up the request that triggered an Error
        const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
        return errorMessage;
    }
};

// Ajouter l'interface DashboardData
export interface DashboardData {
    totalFlights: number;
    totalFlightTime: number;
    totalDistance: number;
    maxAltitude: number;
    maxFlightTime: number;
    lastMonths: {
        month: string;
        label: string;
        flightCount: number;
        flightTime: number;
    }[];
    yearlyStats: {
        year: string;
        flightCount: number;
    }[];
    latestFlights: {
        id: number;
        startDate: string;
        duration: number;
        maxAltitude: number;
        takeoffLocation: string;
        landingLocation: string;
        distance: number;
    }[];
    takeoffLocationsStats: {
        takeoffLocation: string;
        latitude: number;
        longitude: number;
        flightCount: number;
    }[];
}

interface GliderService {
    getGliders: () => Promise<Glider[]>;
    getGliderById: (id: string) => Promise<Glider>;
    createGlider: (glider: Partial<Glider>) => Promise<Glider>;
    updateGlider: (id: string, glider: Partial<Glider>) => Promise<Glider>;
    deleteGlider: (id: string) => Promise<boolean>;
}

export const gliderService: GliderService = {
    getGliders: async () => {
        const response = await api.get<{ success: boolean; data: { gliders: Glider[] } }>('/api/gliders');
        return response.data.data?.gliders || [];
    },

    getGliderById: async (id: string) => {
        const response = await api.get<{ success: boolean; data: { glider: Glider } }>(`/api/gliders/${id}`);
        return response.data.data.glider;
    },

    createGlider: async (glider: Partial<Glider>) => {
        const response = await api.post<{ success: boolean; data: Glider }>('/api/gliders', glider);
        return response.data.data;
    },

    updateGlider: async (id: string, glider: Partial<Glider>) => {
        const response = await api.put<{ success: boolean; data: Glider }>(`/api/gliders/${id}`, glider);
        return response.data.data;
    },

    deleteGlider: async (id: string) => {
        await api.delete(`/api/gliders/${id}`);
        return true;
    }
};

// Favorite Balises Service
interface FavoriteBaliseService {
    getFavoriteBalises: () => Promise<FavoriteBalise[]>;
    addFavoriteBalise: (request: FavoriteBaliseCreateRequest) => Promise<FavoriteBalise>;
    removeFavoriteBalise: (id: number) => Promise<void>;
    removeFavoriteBaliseByBaliseId: (baliseId: number) => Promise<void>;
    reorderFavoriteBalises: (request: FavoriteBaliseReorderRequest) => Promise<void>;
    toggleFavoriteBalise: (baliseId: number) => Promise<{ isFavorite: boolean; favorite?: FavoriteBalise }>;
}

export const favoriteBaliseService: FavoriteBaliseService = {
    getFavoriteBalises: async () => {
        const response = await api.get<FavoriteBalise[]>('/api/favorite-balises');
        // L'API retourne directement un tableau
        return Array.isArray(response.data) ? response.data : [];
    },

    addFavoriteBalise: async (request: FavoriteBaliseCreateRequest) => {
        const response = await api.post<FavoriteBalise>('/api/favorite-balises', request);
        return response.data;
    },

    removeFavoriteBalise: async (id: number) => {
        await api.delete(`/api/favorite-balises/${id}`);
    },

    removeFavoriteBaliseByBaliseId: async (baliseId: number) => {
        await api.delete(`/api/favorite-balises/by-balise/${baliseId}`);
    },

    reorderFavoriteBalises: async (request: FavoriteBaliseReorderRequest) => {
        await api.put('/api/favorite-balises/reorder', request);
    },

    toggleFavoriteBalise: async (baliseId: number) => {
        logger.debug('Toggling favorite balise with ID', { baliseId });
        const response = await api.post<{ isFavorite: boolean; favorite?: FavoriteBalise }>(
            `/api/favorite-balises/toggle/${baliseId}`
        );
        logger.debug('Toggle favorite balise response', response.data);
        return response.data;
    }
};

// Favorite Meteo Spots Service
interface FavoriteMeteoSpotService {
    getFavoriteMeteoSpots: () => Promise<FavoriteMeteoSpot[]>;
    addFavoriteMeteoSpot: (request: FavoriteMeteoSpotCreateRequest) => Promise<FavoriteMeteoSpot>;
    updateFavoriteMeteoSpot: (id: number, request: FavoriteMeteoSpotUpdateRequest) => Promise<FavoriteMeteoSpot>;
    removeFavoriteMeteoSpot: (id: number) => Promise<void>;
    reorderFavoriteMeteoSpots: (request: FavoriteMeteoSpotReorderRequest) => Promise<void>;
}

export const favoriteMeteoSpotService: FavoriteMeteoSpotService = {
    getFavoriteMeteoSpots: async () => {
        const response = await api.get<FavoriteMeteoSpot[]>('/api/favorite-meteo-spots');
        // L'API retourne directement un tableau
        return Array.isArray(response.data) ? response.data : [];
    },

    addFavoriteMeteoSpot: async (request: FavoriteMeteoSpotCreateRequest) => {
        const response = await api.post<FavoriteMeteoSpot>('/api/favorite-meteo-spots', request);
        return response.data;
    },

    updateFavoriteMeteoSpot: async (id: number, request: FavoriteMeteoSpotUpdateRequest) => {
        const response = await api.put<FavoriteMeteoSpot>(`/api/favorite-meteo-spots/${id}`, request);
        return response.data;
    },

    removeFavoriteMeteoSpot: async (id: number) => {
        await api.delete(`/api/favorite-meteo-spots/${id}`);
    },

    reorderFavoriteMeteoSpots: async (request: FavoriteMeteoSpotReorderRequest) => {
        await api.put('/api/favorite-meteo-spots/reorder', request);
    }
};

// User preferences service
export const userService = {
    /** Profil complet, y compris les préférences (contrairement à l'utilisateur reconstruit du JWT). */
    getProfile: async (): Promise<User> => {
        const response = await api.get<User>('/api/users/profile');
        return response.data;
    },

    /**
     * Opt-out des statistiques communautaires de site. L'effet est différé : les vols ne
     * sortent de l'agrégat qu'au prochain batch `app:compute-thermal-hotspots` côté backend.
     */
    updatePreferences: async (preferences: { excludedFromSiteStats?: boolean }): Promise<{ excludedFromSiteStats: boolean }> => {
        const response = await api.patch<{ excludedFromSiteStats: boolean }>('/api/users/preferences', preferences);
        return response.data;
    }
};
